import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { i18n } from '@/utils/i18n';

import { signUpNpoDonation } from '../functions/chatRooms';
import {
  contactNpoRefund,
  donateToNpo,
  donationImpact,
  userImpact,
  userImpactMap,
  userImpactScrolling
} from '../functions/donations';

export const useSignUpNpoDonation = () =>
  useMutation({
    mutationKey: ['sign-up-npo-donation'],
    mutationFn: signUpNpoDonation
  });

export const useDonateToNpo = () => {
  const { showError } = useEndFlowModal();
  return useMutation({
    mutationFn: donateToNpo,
    mutationKey: ['donate-to-npo'],
    onError: (e: AxiosError) => {
      const errorData = e.response?.data as { message: string };

      if (e.status === 400) {
        const rawError = (e.response?.data as any).response?.apiOneTimePayment?.error;

        if (typeof rawError === 'string') {
          try {
            const parsed = JSON.parse(rawError);
            const errorDetail =
              parsed?.response?.details?.result_message ?? parsed?.response?.details?.result_description;

            if (typeof errorDetail === 'string' && errorDetail.trim()) {
              const msg = i18n.t('donation_summary.api_error', { ERROR_DETAIL: errorDetail });

              showError({
                title: e.message,
                message: msg
              });
              console.error('Error donating to npo', e);
            } else {
              showError({
                title: e.message,
                message: errorData?.message
              });
              console.error('Error donating to npo', e);
            }
          } catch {
            showError({
              title: e.message,
              message: errorData?.message
            });
            console.error('Error donating to npo', e);
          }
        }
      } else {
        showError({
          title: e.message,
          message: errorData?.message
        });
        console.error('Error donating to npo', e);
      }
    }
  });
};

export const useGetUserImpactScrolling = (params: { n: string; limit: number; npoType: string; periodTime: string }) =>
  useInfiniteQuery({
    queryKey: ['get-user-impact-scrolling', params.n, params.limit, params.npoType, params.periodTime],
    queryFn: userImpactScrolling,
    initialPageParam: params.n?.toString(),
    getNextPageParam: (lastPage) => {
      if (lastPage.last) {
        return lastPage.last;
      } else {
        return null;
      }
    }
  });

export const useGetUserImpact = ({ npoType, periodTime }: { npoType: string; periodTime: string }) =>
  useQuery({
    queryKey: ['get-user-impact', npoType, periodTime],
    queryFn: () => userImpact({ npoType, periodTime }),
    enabled: !!npoType && !!periodTime
  });

export const useGetUserImpactMap = () => {
  const { showError } = useEndFlowModal();
  return useMutation({
    mutationKey: ['get-user-impact-map'],
    mutationFn: userImpactMap,
    onError: (e) => {
      showError({});
      console.error('Error getting user impact map', e);
    }
  });
};

export const useContactNpoRefund = () => {
  const { showError } = useEndFlowModal();
  return useMutation({
    mutationKey: ['contact-npo-refund'],
    mutationFn: contactNpoRefund,
    onError: (e) => {
      showError({});
      console.error('Error contacting npo for refund', e);
    }
  });
};

export const useGetDonationImpact = ({ userId }: { userId: string }) =>
  useQuery({
    queryKey: ['get-donation-impact', userId],
    queryFn: () => donationImpact({ userId }),
    enabled: !!userId
  });
